import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from '../pages/Navbar';
import DOMPurify from 'dompurify'; 

// Fixed WhatsApp contact number used for every job (instead of the employer's own number).
// wa.me requires the number in international format with no leading + or 0.
const WHATSAPP_NUMBER = '917904949969';

const decodeEntities = (str) => {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
};

const toSafeHtml = (raw) => {
  if (!raw) return '';
  let html = raw;
  if (/&lt;\/?[a-z][\s\S]*?&gt;/i.test(html)) {
    html = decodeEntities(decodeEntities(html));
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const [insight, setInsight] = useState(null);
const [loadingInsight, setLoadingInsight] = useState(false);
const [salaryInsight, setSalaryInsight] = useState(null);

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [alreadyApplied, setAlreadyApplied] = useState(false);

  const [profile, setProfile] = useState(null);
  const [resumeChoice, setResumeChoice] = useState('existing'); // 'existing' | 'new'
  const [newResumeFile, setNewResumeFile] = useState(null);

  const [coverLetterMode, setCoverLetterMode] = useState('write'); // 'write' | 'upload'
  const [coverLetterFile, setCoverLetterFile] = useState(null);

  const [willingToRelocate, setWillingToRelocate] = useState(false);
  

  // --- Report state ---
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSent, setReportSent] = useState(false);

  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await api.get(`/jobs/${id}`);
        const data = response.data.data;
        setJob(data);
        setEmployer(data.employer);
        setSimilarJobs(data.similar_jobs || []);
        setSaved(!!data.saved);
      } catch (err) {
        console.error('Failed to fetch job', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);


 useEffect(() => {
  let ignore = false;

  const fetchInsight = async () => {
    if (!user || user.role !== 'job_seeker' || !job?.description) return;
    setLoadingInsight(true);
    try {
      const res = await api.post('/profile/ats-score', { job_description: job.description });
      if (!ignore) setInsight(res.data);
    } catch (err) {
      if (!ignore) console.error('Failed to fetch resume insight', err);
    } finally {
      if (!ignore) setLoadingInsight(false);
    }
  };

  fetchInsight();
  return () => { ignore = true; };
}, [job, user]);

useEffect(() => {
  const fetchSalaryInsight = async () => {
    if (!user || user.role !== 'job_seeker') return;
    try {
      const res = await api.get(`/jobs/${id}/salary-insight`);
      setSalaryInsight(res.data);
    } catch (err) {
      console.error('Failed to fetch salary insight', err);
    }
  };
  fetchSalaryInsight();
}, [id, user]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || user.role !== 'job_seeker') return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await api.get('/profile');
        setProfile(response.data);
        if (!response.data?.resume_path) setResumeChoice('new');
      } catch (err) {
        console.error('Failed to fetch profile', err);
      }
    };
    fetchProfile();
  }, [user]);

  useEffect(() => {
    const checkIfApplied = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await api.get('/my-applications');
        // A withdrawn application for this job should not block re-applying --
        // only a live (non-withdrawn) application counts as "already applied".
        const applied = res.data.some(
          (application) =>
            application.job_posting_id === parseInt(id) && application.status !== 'withdrawn'
        );
        setAlreadyApplied(applied);
      } catch (err) {
        console.error('Failed to check application status', err);
      }
    };
    checkIfApplied();
  }, [id]);

  const handleApply = async (e) => {
  e.preventDefault();

  if (resumeChoice === 'new' && !newResumeFile) {
    setMessage('Please upload a resume to apply.');
    setMessageType('error');
    return;
  }
  if (coverLetterMode === 'upload' && !coverLetterFile) {
    setMessage('Please upload a cover letter PDF, or switch to writing one.');
    setMessageType('error');
    return;
  }

    

  setApplying(true);
  setMessage('');

  const token = localStorage.getItem('token');
  if (!token) {
    navigate('/login');
    return;
  }

  const needsMultipart = resumeChoice === 'new' || coverLetterMode === 'upload';
  const trimmedCoverLetter = coverLetter.trim();

  try {
    if (needsMultipart) {
      const formData = new FormData();
      if (coverLetterMode === 'upload') {
        formData.append('cover_letter_file', coverLetterFile);
      } else if (trimmedCoverLetter !== '') {
        // only append if the user actually wrote something
        formData.append('cover_letter', trimmedCoverLetter);
      }
      if (resumeChoice === 'new') {
        formData.append('resume', newResumeFile);
      }
      formData.append('willing_to_relocate', willingToRelocate ? '1' : '0');
      await api.post(`/jobs/${id}/apply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      await api.post(`/jobs/${id}/apply`, {
        ...(trimmedCoverLetter !== '' ? { cover_letter: trimmedCoverLetter } : {}),
        willing_to_relocate: willingToRelocate,
      });
    }
    setMessage('Application submitted successfully!');
    setMessageType('success');
    setAlreadyApplied(true);
  } catch (err) {
    const msg = err.response?.data?.message || 'Failed to apply.';
    setMessage(msg);
    setMessageType('error');
    if (msg.toLowerCase().includes('already applied')) {
      setAlreadyApplied(true);
    }
  } finally {
    setApplying(false);
  }
};

  // Toggle save / unsave
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (saved) {
        await api.delete('/jobs/save', { data: { job_posting_id: Number(id) } });
        setSaved(false);
      } else {
        await api.post('/jobs/save', { job_posting_id: Number(id) });
        setSaved(true);
      }
    } catch (err) {
      console.error('Failed to update saved status', err);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: job?.title, url });
      } catch (err) {
        // user cancelled the share sheet — nothing to do
      }
    } else {
      await navigator.clipboard.writeText(url);
      setMessage('Link copied to clipboard');
      setMessageType('success');
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const submitReport = async () => {
    if (!reportReason) return;
    try {
      await api.post(`/jobs/${id}/report`, { reason: reportReason });
      setReportSent(true);
      setShowReportForm(false);
    } catch (err) {
      console.error('Failed to submit report', err);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;
  if (!job) return <div className="p-10 text-center text-gray-500">Job not found.</div>;

  const logoSrc = employer?.company_logo
    ? (employer.company_logo.startsWith('http')
        ? employer.company_logo
        : `${BACKEND_URL}/storage/${employer.company_logo}`)
    : null;

  const skills = Array.isArray(job.skills_required)
    ? job.skills_required
    : (job.skills_required || '').split(',').map((s) => s.trim()).filter(Boolean);

  // WhatsApp message text — the number itself is now fixed (WHATSAPP_NUMBER above),
  // so every job posting uses the same WhatsApp contact regardless of employer phone.
  const whatsappMessage = encodeURIComponent(`Hi, I'm interested in the ${job.title} position.`);

 const renderSimilarJobCard = (sj) => {
  const sjLogoSrc = sj.employer?.company_logo
    ? (sj.employer.company_logo.startsWith('http')
        ? sj.employer.company_logo
        : `${BACKEND_URL}/storage/${sj.employer.company_logo}`)
    : null;

  return (
    <div
      key={sj.id}
      className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-[#9333EA] transition"
    >
      <button
        onClick={() => navigate(`/jobs/${sj.id}`)}
        className="w-full text-left"
      >
        <div className="flex items-center gap-3">
          {sjLogoSrc ? (
            <img src={sjLogoSrc} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100 shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-800 truncate">{sj.title}</p>
            <p className="text-sm text-gray-500 truncate">{sj.company_name} · {sj.location}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {sj.job_type && (
            <span className="bg-secondary-green text-primary-green text-xs font-medium px-2.5 py-1 rounded-full">
              {sj.job_type.replace('_', ' ')}
            </span>
          )}
          {sj.work_mode && (
            <span className="bg-purple-50 text-[#9333EA] text-xs font-medium px-2.5 py-1 rounded-full">
              {sj.work_mode.replace(/_/g, ' ')}
            </span>
          )}
          {sj.experience_required != null && (
            <span className="bg-secondary-orange text-primary-orange text-xs font-medium px-2.5 py-1 rounded-full">
              {sj.experience_required}+ yrs
            </span>
          )}
        </div>

        {sj.skills_required && (
          <p className="text-xs text-gray-400 mt-2 truncate">🧩 {sj.skills_required}</p>
        )}

        {sj.openings != null && (
          <p className="text-xs text-gray-400 mt-1">👥 {sj.openings} opening{sj.openings === 1 ? '' : 's'}</p>
        )}
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/jobs/${sj.id}`);
        }}
        className="mt-3 w-full text-sm font-semibold px-3 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white hover:opacity-90"
      >
        Apply Now
      </button>
    </div>
  );
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left sidebar — Similar Jobs */}
          <div className="w-72 shrink-0 sticky top-8 hidden md:block">
            <div className="bg-white p-5 rounded-lg shadow">
              <h3 className="font-semibold text-[#1E1033] mb-3">Similar jobs</h3>
              {similarJobs.length > 0 ? (
                <div className="space-y-2">
                  {similarJobs.map((sj) => renderSimilarJobCard(sj))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No similar jobs found yet.</p>
              )}
            </div>
          </div>

          {/* Main content — single merged card */}
          <div className="flex-1 w-full min-w-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-8">
              {/* Header row: logo, badges, title, save/share */}
              <div className="flex items-start gap-3">
                {logoSrc ? (
                  <img src={logoSrc} alt="" className="w-11 h-11 rounded-full object-cover shrink-0 border border-gray-100" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] flex items-center justify-center font-bold text-white shrink-0">
                    {job.company_name?.charAt(0)?.toUpperCase() || 'J'}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                      {job.job_type?.replace(/_/g, ' ') || 'Open'}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-[#9333EA]">
                      New
                    </span>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-bold text-gray-900 leading-snug text-lg">{job.title}</h2>
                      <p className="text-sm text-gray-500">{job.company_name}</p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                      {/* Call button removed per request — WhatsApp only, fixed number */}

                      {/* WhatsApp — always shown, fixed contact number for every job */}
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#25D366]"
                        title="Message on WhatsApp"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.51 2 12.04 2zm5.8 14.02c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.12.11-1.8-.11a17.6 17.6 0 0 1-1.66-.61c-2.94-1.27-4.86-4.24-5-4.44-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37h.57c.18 0 .43-.07.67.51.24.58.83 2.01.9 2.15.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.61.17.3.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.3.15.47.13.65-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.67-.15.27.1 1.72.81 2.01.96.3.15.5.23.57.35.08.13.08.72-.16 1.4z" />
                        </svg>
                      </a>

                      {/* Share */}
                      <button
                        onClick={handleShare}
                        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700"
                        title={copied ? 'Copied!' : 'Share'}
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                        </svg>
                      </button>

                      {/* Save */}
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-xl disabled:opacity-50 ${
                          saved ? 'text-[#DB2777]' : 'text-gray-700'
                        }`}
                        title={saved ? 'Saved (click to unsave)' : 'Save'}
                      >
                        {saved ? '★' : '☆'}
                      </button>

                      {job.salary_max && (
                        <span className="text-sm font-bold text-gray-900 ml-1 whitespace-nowrap">
                          ₹{Math.round(job.salary_max / 1000)}K
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {job.work_mode && <span>{job.work_mode.replace(/_/g, ' ')}</span>}
                    {job.job_type && <span>· {job.job_type.replace(/_/g, ' ')}</span>}
                    {job.experience_required != null && <span>· {job.experience_required}+ yrs exp</span>}
                    {job.openings != null && <span>· {job.openings} opening{job.openings === 1 ? '' : 's'}</span>}
                  </p>
                </div>
              </div>

              {/* Skills match row */}
             {(user?.role === 'job_seeker') && (
  <div className="mt-3">
    {loadingInsight ? (
      <p className="text-xs text-gray-400">Checking your fit for this role…</p>
    ) : insight ? (
      <div className="flex flex-col gap-1.5 text-xs">
        {insight.matched_keywords?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap font-medium text-gray-500">
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-green-500">✓</span>
            Your match!
            <span className="flex flex-wrap gap-1 ml-1">
              {insight.matched_keywords.map((s, i) => (
                <span key={i} className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">✓ {s}</span>
              ))}
            </span>
          </div>
        )}
        {insight.missing_keywords?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap font-medium text-gray-500">
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-amber-500">!</span>
            Worth adding
            <span className="flex flex-wrap gap-1 ml-1">
              {insight.missing_keywords.map((s, i) => (
                <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{s}</span>
              ))}
            </span>
          </div>
        )}
        {insight.suggestions?.length > 0 && (
          <ul className="mt-1 list-disc list-inside text-gray-500 space-y-0.5">
            {insight.suggestions.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        )}
      </div>
    ) : null}
  </div>
)}
              {/* 3-column layout: meta / description / share panel */}
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-[140px_1fr_200px] gap-4">
                <div className="space-y-1.5 text-xs text-gray-500">
                  <p>📍 {job.location || 'Not specified'}</p>
                  {job.work_mode && <p>🏠{job.work_mode.replace(/_/g, ' ')}</p>}
                  {job.job_type && <p>⏰ {job.job_type.replace(/_/g, ' ')}</p>}
                  {job.experience_required != null && <p>💼{job.experience_required}+ yrs exp</p>}
                  <p>
                    💰 ₹{Number(job.salary_min).toLocaleString()} - ₹{Number(job.salary_max).toLocaleString()}
                  </p>
                  {salaryInsight && (
  <p className={
    salaryInsight.verdict === 'below_market' ? 'text-amber-600' :
    salaryInsight.verdict === 'above_market' ? 'text-green-600' :
    'text-gray-400'
  }>
    {salaryInsight.verdict === 'no_baseline'
      ? 'Add your expected salary to see how this compares'
      : `📊 ${salaryInsight.note}`}
  </p>
)}
                </div>

               <div>
  <p className="text-xs font-semibold text-gray-400 uppercase mb-1">What you'll be working on</p>
  <div
  className="text-sm text-gray-600 leading-relaxed [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
  dangerouslySetInnerHTML={{ __html: toSafeHtml(job.description) }}
/>

  {job.benefits && (
    <>
      <p className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-1">Benefits</p>
      <div
        className="text-sm text-gray-600"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.benefits) }}
      />
    </>
  )}

                  {employer?.company_about && (
                    <>
                      <p className="text-xs font-semibold text-gray-400 uppercase mt-4 mb-1">About {job.company_name}</p>
                      <p className="text-sm text-gray-600">{employer.company_about}</p>
                      {employer.company_website && (
                        <a href={employer.company_website} target="_blank" rel="noreferrer" className="text-xs text-[#9333EA] hover:underline break-all">
                          {employer.company_website}
                        </a>
                      )}
                    </>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-3 h-fit space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-2">Contact employer</p>
                    <div className="flex items-center gap-1.5">
                      {/* Call button removed per request */}
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-[#25D366] text-white"
                      >
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.51 2 12.04 2zm5.8 14.02c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.12.11-1.8-.11a17.6 17.6 0 0 1-1.66-.61c-2.94-1.27-4.86-4.24-5-4.44-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37h.57c.18 0 .43-.07.67.51.24.58.83 2.01.9 2.15.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.61.17.3.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.3.15.47.13.65-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.67-.15.27.1 1.72.81 2.01.96.3.15.5.23.57.35.08.13.08.72-.16 1.4z" />
                        </svg>
                        WhatsApp
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-2">↗ Share this job</p>
                    <div className="flex items-center gap-1.5">
                      <input
                        readOnly
                        value={window.location.href}
                        className="flex-1 min-w-0 text-[11px] border rounded-lg px-2 py-1.5 bg-white text-gray-500 truncate"
                      />
                      <button
                        onClick={handleCopyLink}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-gray-900 text-white whitespace-nowrap shrink-0"
                      >
                        {copied ? 'Copied!' : 'Copy link'}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 leading-snug">
                      Grants are not eligible for referral bonuses at this time.
                    </p>
                  </div>
                </div>
              </div>


              {/* Internal linking block — connects job → employer, location, category */}
<div className="mt-4 pt-4 border-t border-gray-100">
  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Explore more</p>
  <div className="flex flex-wrap gap-2 text-sm">
    {employer?.company_slug && (
      <Link
        to={`/company/${employer.company_slug}`}
        className="px-3 py-1.5 rounded-full bg-gray-50 text-gray-700 hover:bg-gray-100"
      >
        More jobs at {job.company_name}
      </Link>
    )}
    {job.location_slug && (
      <Link
        to={`/jobs/${job.location_slug}`}
        className="px-3 py-1.5 rounded-full bg-gray-50 text-gray-700 hover:bg-gray-100"
      >
        More jobs in {job.location}
      </Link>
    )}
    {job.category && (
      <Link
        to={`/jobs/${job.category.slug}`}
        className="px-3 py-1.5 rounded-full bg-gray-50 text-gray-700 hover:bg-gray-100"
      >
        More {job.category.name} jobs
      </Link>
    )}
    {job.location_slug && job.category && (
      <Link
        to={`/jobs/${job.location_slug}/${job.category.slug}`}
        className="px-3 py-1.5 rounded-full bg-gray-50 text-gray-700 hover:bg-gray-100"
      >
        {job.category.name} jobs in {job.location}
      </Link>
    )}
  </div>
</div>

              {/* Apply section */}
              {(!user || user.role === 'job_seeker') && (
                <>
                  <hr className="my-6" />

                  {alreadyApplied ? (
                    <div className="bg-secondary-green text-primary-green px-4 py-3 rounded-lg text-sm font-semibold">
                      ✓ You have already applied to this job
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-[#1E1033] mb-3">Apply to this job</h3>

                      {message && (
                        <div
                          className={`p-3 rounded mb-4 text-sm ${
                            messageType === 'success'
                              ? 'bg-secondary-green text-primary-green'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {message}
                        </div>
                      )}

                      <form onSubmit={handleApply}>
                        <div className="mb-4">
                          <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Resume</label>

                          {profile?.resume_path ? (
                            <div className="space-y-2">
                              <label
                                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 border rounded px-3 py-2 cursor-pointer ${
                                  resumeChoice === 'existing' ? 'border-[#9333EA] ring-1 ring-[#9333EA]' : 'border-gray-200'
                                }`}
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="radio"
                                    name="resumeChoice"
                                    checked={resumeChoice === 'existing'}
                                    onChange={() => setResumeChoice('existing')}
                                    className="accent-[#9333EA] shrink-0"
                                  />
                                  <span className="text-sm text-gray-700 truncate">Use the resume on my profile</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    window.open(
                                      profile.resume_path.startsWith('http')
                                        ? profile.resume_path
                                        : `${BACKEND_URL}/storage/${profile.resume_path}`,
                                      '_blank'
                                    )
                                  }
                                  className="text-[#9333EA] text-xs font-semibold hover:underline shrink-0 self-start sm:self-auto"
                                >
                                  View
                                </button>
                              </label>

                              <label
                                className={`flex items-center gap-2 border rounded px-3 py-2 cursor-pointer ${
                                  resumeChoice === 'new' ? 'border-[#9333EA] ring-1 ring-[#9333EA]' : 'border-gray-200'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="resumeChoice"
                                  checked={resumeChoice === 'new'}
                                  onChange={() => setResumeChoice('new')}
                                  className="accent-[#9333EA] shrink-0"
                                />
                                <span className="text-sm text-gray-700">Upload a different resume for this application</span>
                              </label>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 mb-2">
                              No resume on file yet — upload one below, or{' '}
                              <Link to="/profile" className="text-[#9333EA] hover:underline">
                                add it to your profile
                              </Link>{' '}
                              to reuse it next time.
                            </p>
                          )}

                          {resumeChoice === 'new' && (
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => setNewResumeFile(e.target.files[0])}
                              className="mt-2 text-sm w-full sm:w-auto"
                            />
                          )}
                        </div>

                   <div className="mb-4">
                          <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">
                            Cover Letter <span className="normal-case font-normal text-gray-400">(optional)</span>
                          </label>

                          <div className="flex gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => setCoverLetterMode('write')}
                              className={`flex-1 sm:flex-none text-sm font-semibold px-3 py-1.5 rounded ${
                                coverLetterMode === 'write'
                                  ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Write
                            </button>
                            <button
                              type="button"
                              onClick={() => setCoverLetterMode('upload')}
                              className={`flex-1 sm:flex-none text-sm font-semibold px-3 py-1.5 rounded ${
                                coverLetterMode === 'upload'
                                  ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Upload PDF
                            </button>
                          </div>

                          {coverLetterMode === 'write' ? (
                            <textarea
                              value={coverLetter}
                              onChange={(e) => setCoverLetter(e.target.value)}
                              placeholder="Write a short cover letter (optional)"
                              rows={4}
                              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                            />
                          ) : (
                            <div>
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => setCoverLetterFile(e.target.files[0])}
                                className="text-sm w-full sm:w-auto"
                              />
                              {coverLetterFile && <p className="text-xs text-gray-500 mt-1">{coverLetterFile.name}</p>}
                            </div>
                          )}
                        </div>

                        <label className="flex items-center gap-2 mb-4 cursor-pointer w-fit">
                          <input
                            type="checkbox"
                            checked={willingToRelocate}
                            onChange={(e) => setWillingToRelocate(e.target.checked)}
                            className="accent-[#9333EA] w-4 h-4"
                          />
                          <span className="text-sm text-gray-700">I am willing to relocate for this job</span>
                        </label>

                        <button
                          type="submit"
                          disabled={applying}
                          className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white font-semibold px-6 py-2 rounded hover:opacity-90 disabled:opacity-50"
                        >
                          {applying ? 'Submitting...' : 'Apply Now'}
                        </button>
                      </form>
                    </>
                  )}

                  <div className="mt-6">
                    {!reportSent ? (
                      <>
                        <button
                          onClick={() => setShowReportForm((v) => !v)}
                          className="text-xs text-gray-400 hover:text-gray-600 underline"
                        >
                          Report this job
                        </button>
                        {showReportForm && (
                          <div className="mt-2 p-3 rounded border border-gray-200">
                            <select
                              value={reportReason}
                              onChange={(e) => setReportReason(e.target.value)}
                              className="w-full border border-gray-200 rounded p-2 text-sm mb-2"
                            >
                              <option value="">Select a reason</option>
                              <option value="spam">Spam or misleading</option>
                              <option value="fraud">Looks fraudulent</option>
                              <option value="expired">Job already filled/expired</option>
                              <option value="other">Other</option>
                            </select>
                            <button onClick={submitReport} className="w-full sm:w-auto text-sm px-3 py-1.5 rounded bg-gray-800 text-white">
                              Submit report
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-green-600">Report submitted — thank you.</p>
                    )}
                  </div>
                </>
              )}

              {user && user.role === 'employer' && (
                <p className="mt-6 text-sm text-gray-500 italic">
                  Employers cannot apply to job postings.
                </p>
              )}

              {user && user.role === 'admin' && (
                <p className="mt-6 text-sm text-gray-500 italic">
                  Admin view — application form is hidden for admin accounts.
                </p>
              )}

              {/* Similar jobs — mobile fallback */}
              {similarJobs.length > 0 && (
                <div className="md:hidden">
                  <hr className="my-6" />
                  <h3 className="font-semibold text-[#1E1033] mb-2">Similar jobs</h3>
                  <div className="space-y-2">
                    {similarJobs.map((sj) => renderSimilarJobCard(sj))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobDetail;